/**
 * Which Google product a link points at.
 *
 * The rail under a creator's name holds their Google things: the Reddit sheet,
 * the socials folder, whatever else somebody pastes. Google's URLs say plainly
 * which product they are, so nobody should have to pick from a dropdown.
 */

export type WorkspaceKind = "drive" | "sheets" | "docs" | "slides" | "forms";

const LABELS: Record<WorkspaceKind, string> = {
  drive: "Folder",
  sheets: "Sheet",
  docs: "Doc",
  slides: "Slides",
  forms: "Form",
};

/**
 * `docs.google.com` serves four different products off one host, separated only
 * by the first path segment, so the host alone is not enough to tell them
 * apart. Drive is the fallback for any other Google host, which covers folders,
 * shared drives and `drive.google.com/open?id=` style links alike.
 */
export function workspaceKind(url: string): WorkspaceKind {
  let parsed: URL;
  try {
    parsed = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
  } catch {
    return "drive";
  }

  const host = parsed.hostname.toLowerCase();
  const segment = parsed.pathname.split("/").filter(Boolean)[0]?.toLowerCase() ?? "";

  if (host.endsWith("docs.google.com")) {
    if (segment === "spreadsheets") return "sheets";
    if (segment === "document") return "docs";
    if (segment === "presentation") return "slides";
    if (segment === "forms") return "forms";
  }

  if (host.endsWith("sheets.google.com")) return "sheets";
  if (host.endsWith("forms.gle") || host.endsWith("forms.google.com")) return "forms";

  return "drive";
}

export function workspaceLabel(kind: WorkspaceKind): string {
  return LABELS[kind];
}

/** True for anything Google hosts, which is all this rail is meant to hold. */
export function isWorkspaceLink(url: string): boolean {
  try {
    const host = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
    return /(^|\.)(google\.com|goo\.gl|forms\.gle)$/.test(host);
  } catch {
    return false;
  }
}
